import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  ChartBarIcon,
  ArrowPathIcon,
  ArchiveBoxIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import * as mcquizApi from '../services/mcquizApi';

const STATUS_BADGES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  preview: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  published: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  archived: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

export default function McQuizTestList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['mcquiz-tests', statusFilter],
    queryFn: () => mcquizApi.listTests(statusFilter || undefined).then((r) => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (testId: number) => mcquizApi.deleteTest(testId),
    onSuccess: () => {
      toast.success('Test deleted');
      queryClient.invalidateQueries({ queryKey: ['mcquiz-tests'] });
    },
    onError: () => toast.error('Failed to delete test'),
  });

  const publishMutation = useMutation({
    mutationFn: (testId: number) => mcquizApi.publishTest(testId),
    onSuccess: () => {
      toast.success('Test published');
      queryClient.invalidateQueries({ queryKey: ['mcquiz-tests'] });
    },
    onError: () => toast.error('Failed to publish test'),
  });

  const archiveMutation = useMutation({
    mutationFn: (testId: number) => mcquizApi.archiveTest(testId),
    onSuccess: () => {
      toast.success('Test archived');
      queryClient.invalidateQueries({ queryKey: ['mcquiz-tests'] });
    },
    onError: () => toast.error('Failed to archive test'),
  });

  const previewMutation = useMutation({
    mutationFn: (testId: number) => mcquizApi.moveToPreview(testId),
    onSuccess: () => {
      toast.success('Moved to preview');
      queryClient.invalidateQueries({ queryKey: ['mcquiz-tests'] });
    },
    onError: () => toast.error('Failed to move to preview'),
  });

  const tests: any[] = data?.items ?? data?.tests ?? (Array.isArray(data) ? data : []);

  const handleDelete = (testId: number, title: string) => {
    if (window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      deleteMutation.mutate(testId);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">MCQuiz Tests</h1>
        <button
          onClick={() => navigate('/mcquiz/tests/new')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Create Test
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
        >
          <option value="">All</option>
          <option value="draft">Draft</option>
          <option value="preview">Preview</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="py-16 text-center text-red-500">Failed to load tests</div>
        ) : tests.length === 0 ? (
          <div className="py-16 text-center text-gray-500 dark:text-gray-400">
            No tests found. Create your first test to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Title', 'Type', 'Status', 'Questions', 'Timer', 'Access', 'Category', 'Actions'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {tests.map((test: any) => (
                  <tr key={test.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[240px] truncate">
                      {test.title}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 capitalize">
                      {(test.test_type || '').replace(/_/g, ' ')}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                          STATUS_BADGES[test.status] || STATUS_BADGES.draft
                        }`}
                      >
                        {test.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {test.question_count ?? '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {test.timer_minutes ? `${test.timer_minutes} min` : 'None'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 capitalize">
                      {test.access_level || 'free'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {test.category || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/mcquiz/tests/${test.id}`)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
                          title="Edit"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/mcquiz/tests/${test.id}/preview`)}
                          className="p-1.5 text-gray-500 hover:text-yellow-600 dark:hover:text-yellow-400"
                          title="Preview"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {test.status === 'draft' && (
                          <button
                            onClick={() => previewMutation.mutate(test.id)}
                            className="p-1.5 text-gray-500 hover:text-yellow-600 dark:hover:text-yellow-400"
                            title="Move to Preview"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        )}
                        {(test.status === 'draft' || test.status === 'preview') && (
                          <button
                            onClick={() => publishMutation.mutate(test.id)}
                            className="p-1.5 text-gray-500 hover:text-green-600 dark:hover:text-green-400"
                            title="Publish"
                          >
                            <RocketLaunchIcon className="w-4 h-4" />
                          </button>
                        )}
                        {test.status === 'published' && (
                          <button
                            onClick={() => archiveMutation.mutate(test.id)}
                            className="p-1.5 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                            title="Archive"
                          >
                            <ArchiveBoxIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/mcquiz/analytics?test_id=${test.id}`)}
                          className="p-1.5 text-gray-500 hover:text-purple-600 dark:hover:text-purple-400"
                          title="Analytics"
                        >
                          <ChartBarIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(test.id, test.title)}
                          className="p-1.5 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
